/**
 * Geo-referenced design canvas: live Esri World Imagery tiles, pan/zoom,
 * multi-surface boundary & obstacle drawing, rotated module grids and
 * drag-to-move modules. Layout preview uses the SAME @pv/engine autoLayout
 * as the server — client preview and server simulation cannot diverge.
 */
import { useEffect, useRef } from "react";
import {
  autoLayout,
  rotatePoint,
  type LayoutOptions,
  type PvModuleSpec,
} from "@pv/engine";
import {
  activeSource,
  attributionFor,
  imageryAtLimit,
  initialBasemapState,
  markBasemapFailed,
  tileUrlFor,
  tileZoomFor,
  type BasemapState,
  type ClientBasemap,
} from "./basemapClient.js";
import {
  northAngleRad,
  panView,
  resetNorth,
  rotateView,
  screenToWorld,
  worldToScreen,
  type View,
} from "./viewTransform.js";
import { useT } from "./i18n/index.js";

// E60: the View (incl. camera rotationDeg) lives in viewTransform.ts — one
// transform, one inverse, unit-tested at 0/30/90/180/270°.
export type { View } from "./viewTransform.js";

const ZOOM_MIN = 1.5;
const ZOOM_MAX = 45;

/** E37: canvas cannot use var() directly — read tokens from the loaded :root
 *  palette at draw time. styles.css remains the single source of truth. */
const themeCache: Record<string, string> = {};
function tk(name: string): string {
  if (!themeCache[name]) {
    themeCache[name] = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  }
  return themeCache[name]!;
}

const CW = 940;
const CH = 560;
const RE = 6378137;

// E63.1: surface types + the canvas→engine conversion live in canvasSurface.ts
// (pure module) so the preview==engine contract is testable on the real path —
// the hardcoded 35° tilt literal that made the toolbar disagree with the
// simulation died there and is source-banned.
import { toEngineSurface, type CanvasGeometry, type CanvasSurface } from "./canvasSurface.js";
export type { CanvasGeometry, CanvasSurface } from "./canvasSurface.js";

export type Tool = "draw" | "obstacle" | "module";

interface Props {
  lat: number;
  lon: number;
  /** E59 — server-decided basemap (per-Bundesland official DOP-WMS or Esri).
   *  Absent/null → Esri; a failing WMS degrades to Esri, never blank. */
  basemap?: ClientBasemap | null;
  surfaces: CanvasSurface[];
  activeSurfaceId: string | null;
  view: View;
  tool: Tool;
  mode: "site" | "layout";
  module?: PvModuleSpec | null;
  layoutOptions?: LayoutOptions;
  onGeometryChange?: (surfaceId: string, g: CanvasGeometry) => void;
  onViewChange: (v: View) => void;
  onToggleModule?: (surfaceId: string, key: string) => void;
  onMoveModule?: (surfaceId: string, key: string, dx: number, dy: number) => void;
  onModuleStats?: (perSurface: Record<string, { count: number; kwp: number }>) => void;
}

const tileCache = new Map<string, HTMLImageElement>();

function ll2m(lat: number, lon: number): { x: number; y: number } {
  return { x: (RE * lon * Math.PI) / 180, y: RE * Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360)) };
}

export function fitViewFor(surfaces: { geometry: CanvasGeometry }[]): View {
  const all = surfaces.flatMap((s) => s.geometry.vertices);
  if (all.length < 3) return { S: 12, cx: 27, cy: 17 };
  const xs = all.map((p) => p.x);
  const ys = all.map((p) => p.y);
  const cx = (Math.min(...xs) + Math.max(...xs)) / 2;
  const cy = (Math.min(...ys) + Math.max(...ys)) / 2;
  const S = Math.max(
    2,
    Math.min(CW / (Math.max(...xs) - Math.min(...xs) + 14), CH / (Math.max(...ys) - Math.min(...ys) + 14), 22),
  );
  return { S: +S.toFixed(2), cx, cy };
}


export default function SiteCanvas(props: Props): JSX.Element {
  const t = useT();
  const ref = useRef<HTMLCanvasElement>(null);
  const stateRef = useRef(props);
  stateRef.current = props;
  /** E59 — basemap fallback state machine (see basemapClient.ts). */
  const basemapRef = useRef<BasemapState>(initialBasemapState(props.basemap ?? null));
  if ((props.basemap ?? null) !== basemapRef.current.source && !(props.basemap == null && basemapRef.current.source.kind === "esri")) {
    basemapRef.current = initialBasemapState(props.basemap ?? null);
  }
  /** Live drag state for module moving (not yet committed). */
  const dragRef = useRef<{
    surfaceId: string;
    key: string;
    startLocal: { x: number; y: number };
    base: { dx: number; dy: number };
    live: { dx: number; dy: number };
  } | null>(null);
  /** E42.2 — live drag state for a boundary vertex (site mode; commits via onGeometryChange). */
  const vertexDragRef = useRef<{ surfaceId: string; index: number } | null>(null);

  useEffect(() => {
    draw();
  });

  function layoutFor(s: CanvasSurface, liveDrag = true) {
    const p = stateRef.current;
    if (!p.module || s.geometry.vertices.length < 3) return null;
    const moved = { ...(s.movedOffsets ?? {}) };
    const d = dragRef.current;
    if (liveDrag && d && d.surfaceId === s.id) {
      moved[d.key] = { dx: d.base.dx + d.live.dx, dy: d.base.dy + d.live.dy };
    }
    return autoLayout(toEngineSurface(s), p.module, p.layoutOptions ?? { orientation: "portrait", moduleGapM: 0.02, rowGapM: 0 }, {
      removedKeys: s.removedKeys,
      movedOffsets: moved,
    });
  }

  function draw(): void {
    const cv = ref.current;
    if (!cv) return;
    const p = stateRef.current;
    const ctx = cv.getContext("2d");
    if (!ctx) return;
    cv.width = CW;
    cv.height = CH;
    const { S, cx, cy } = p.view;
    // E60: the ONE world→screen transform — camera rotation included, every
    // overlay inherits it. Its inverse handles every pointer path (toLocal).
    const M = (q: { x: number; y: number }) => worldToScreen(q, p.view, CW, CH);
    const rot = northAngleRad(p.view);

    ctx.fillStyle = tk("--terrain");
    ctx.fillRect(0, 0, CW, CH);

    // tiles — computed in the unrotated frame, drawn inside a rotated context
    // so the basemap and the M()-drawn overlays share the same camera (E60)
    ctx.save();
    if (rot !== 0) {
      ctx.translate(CW / 2, CH / 2);
      ctx.rotate(rot);
      ctx.translate(-CW / 2, -CH / 2);
    }
    const cosL = Math.cos((p.lat * Math.PI) / 180);
    const aw = ll2m(p.lat, p.lon);
    const AX = 27;
    const AY = 17;
    // E62.2 — per-source native zoom, data-driven from groundResolutionM
    // (20 cm → z19; NRW's 10 cm → z20; Esri's pyramid ends at 19). The old
    // hardcoded min(19, …) was Esri's limit applied to everyone — it showed
    // NRW at half the detail it is entitled to.
    const z = tileZoomFor(basemapRef.current, S, p.lat);
    const mpt = (2 * Math.PI * RE) / Math.pow(2, z);
    const x0 = cx - CW / 2 / S;
    const y0 = cy - CH / 2 / S;
    const m0x = aw.x + (x0 - AX) / cosL;
    const m0y = aw.y - (y0 - AY) / cosL;
    const ppm = cosL * S;
    const tpx = mpt * ppm;
    const tx0 = Math.floor((m0x + Math.PI * RE) / mpt);
    const ty0 = Math.floor((Math.PI * RE - m0y) / mpt);
    // a rotated viewport shows corners outside the unrotated rect — widen the
    // tile range by the half-diagonal margin so no angle shows blank corners
    const extraPx = rot === 0 ? 0 : (Math.hypot(CW, CH) - Math.min(CW, CH)) / 2 + tpx;
    const extraTiles = Math.ceil(extraPx / tpx);
    const bmSource = activeSource(basemapRef.current, z); // z-aware: HH renders Esri above 1:5000
    if (bmSource.kind === "google_solar") {
      // E68-unlock: SOLE basemap — exactly one georeferenced image, NO tiles
      // underneath (compositing Google content over a non-Google map is the
      // gated display_context clause; terrain colour shows beyond the image).
      const img = bmSource.image as HTMLImageElement | undefined;
      if (img && img.complete && img.naturalWidth) {
        const mW = ll2m(0, bmSource.bbox.west).x;
        const mE = ll2m(0, bmSource.bbox.east).x;
        const mN = ll2m(bmSource.bbox.north, 0).y;
        const mS = ll2m(bmSource.bbox.south, 0).y;
        ctx.drawImage(img, (mW - m0x) * ppm, (m0y - mN) * ppm, (mE - mW) * ppm, (mN - mS) * ppm);
      }
    } else
    for (let tx = tx0 - extraTiles; tx <= tx0 + Math.ceil(CW / tpx) + extraTiles; tx++) {
      for (let ty = ty0 - extraTiles; ty <= ty0 + Math.ceil(CH / tpx) + extraTiles; ty++) {
        // E59: cache key carries the provider so a fallback never serves stale
        // pixels under the wrong credit; the shared cache itself is reused (rule 5)
        const key = `${bmSource.providerId}|${z}/${ty}/${tx}`;
        let img = tileCache.get(key);
        if (!img) {
          img = new Image();
          // E59: no crossOrigin on WMS tiles — Land servers often send no CORS
          // headers, and "anonymous" would fail EVERY tile and silently force
          // the Esri fallback. Drawing without it merely taints the canvas,
          // which is harmless here (no pixel readback anywhere — verified).
          // ⚠ E63 / 3D constraint ON RECORD: a WebGL texture upload IS a pixel
          // read, and so is toDataURL(). DOP texturing and customer screenshot
          // export are therefore blocked on every official source until an API
          // tile proxy exists. Recorded for the 3D decision — do not "fix" by
          // re-adding crossOrigin (that breaks 2D on CORS-less Land servers).
          if (bmSource.kind === "esri") img.crossOrigin = "anonymous";
          img.onload = () => draw();
          img.onerror = () => {
            // E59 rule 5: a failing WMS degrades to Esri for the session — never blank
            if (activeSource(basemapRef.current, z).kind === "wms") {
              basemapRef.current = markBasemapFailed(basemapRef.current);
              console.warn(`[basemap] ${bmSource.providerId} tile failed — falling back to Esri World Imagery`);
              draw();
            }
          };
          img.src = tileUrlFor(basemapRef.current, z, ty, tx);
          tileCache.set(key, img);
        }
        if (img.complete && img.naturalWidth) {
          const mx = tx * mpt - Math.PI * RE;
          const my = Math.PI * RE - ty * mpt;
          ctx.drawImage(img, (mx - m0x) * ppm, (m0y - my) * ppm, tpx + 0.8, tpx + 0.8);
        }
      }
    }
    ctx.restore(); // E60: rotation applies to the basemap only via this context

    const stats: Record<string, { count: number; kwp: number }> = {};

    for (const s of p.surfaces) {
      const isActive = s.id === p.activeSurfaceId;
      const g = s.geometry;

      // boundary
      if (g.vertices.length >= 2) {
        ctx.beginPath();
        const a = M(g.vertices[0]!);
        ctx.moveTo(a.x, a.y);
        g.vertices.slice(1).forEach((q) => {
          const c = M(q);
          ctx.lineTo(c.x, c.y);
        });
        if (g.vertices.length > 2) {
          ctx.closePath();
          ctx.fillStyle = isActive ? tk("--roof-fill") : tk("--roof-fill-soft");
          ctx.fill();
        }
        ctx.strokeStyle = isActive ? tk("--canvas-active") : tk("--canvas-active-soft");
        ctx.lineWidth = isActive ? 2.5 : 1.5;
        ctx.stroke();
      }

      // modules (rotated grid → draw world-frame polygons)
      if (p.mode === "layout" && p.module && g.vertices.length > 2) {
        const layout = layoutFor(s);
        if (layout) {
          let active = 0;
          const rot = layout.gridRotationDeg;
          const pivot = layout.gridPivot;
          for (const m of layout.modules) {
            const removed = s.removedKeys?.has(m.key);
            if (!removed) active++;
            const cornersGrid = [
              { x: m.x, y: m.y },
              { x: m.x + m.w, y: m.y },
              { x: m.x + m.w, y: m.y + m.h },
              { x: m.x, y: m.y + m.h },
            ];
            const corners = cornersGrid.map((c) => M(rot ? rotatePoint(c, pivot, rot) : c));
            ctx.beginPath();
            ctx.moveTo(corners[0]!.x, corners[0]!.y);
            for (let i = 1; i < 4; i++) ctx.lineTo(corners[i]!.x, corners[i]!.y);
            ctx.closePath();
            ctx.fillStyle = removed
              ? tk("--canvas-contrast-faint")
              : m.invalid
                ? tk("--panel-invalid-fill")
                : tk("--panel-fill");
            ctx.strokeStyle = removed ? tk("--canvas-contrast-soft") : m.invalid ? tk("--panel-invalid") : tk("--panel-stroke");
            ctx.lineWidth = 1;
            ctx.fill();
            ctx.stroke();
          }
          stats[s.id] = { count: active, kwp: (active * p.module.wp) / 1000 };
        }
      }

      // obstacles
      for (const o of g.obstacles) {
        const q = M(o.center);
        ctx.fillStyle = tk("--obstacle");
        ctx.beginPath();
        ctx.arc(q.x, q.y, o.radiusM * S, 0, 7);
        ctx.fill();
        ctx.strokeStyle = tk("--canvas-contrast");
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }

      // draw-mode vertices for the active surface
      if (p.mode === "site" && isActive) {
        g.vertices.forEach((pt, i) => {
          const q = M(pt);
          ctx.fillStyle = i === 0 ? tk("--canvas-active") : tk("--canvas-contrast");
          ctx.strokeStyle = tk("--canvas-ink");
          ctx.beginPath();
          ctx.arc(q.x, q.y, 5, 0, 7);
          ctx.fill();
          ctx.stroke();
        });
      }

      // surface label
      if (g.vertices.length > 2) {
        const cxm = g.vertices.reduce((a, v) => a + v.x, 0) / g.vertices.length;
        const cym = Math.min(...g.vertices.map((v) => v.y));
        const q = M({ x: cxm, y: cym });
        ctx.font = "bold 11px sans-serif";
        ctx.fillStyle = isActive ? tk("--canvas-active") : tk("--canvas-contrast-strong");
        ctx.fillText(s.id === p.activeSurfaceId ? `▸ ${labelOf(s)}` : labelOf(s), q.x - 20, q.y - 8);
      }
    }

    if (p.mode === "layout") p.onModuleStats?.(stats);

    // north + scale + attribution. E60: the ARROW rotates with the camera (it
    // always points at true screen-north of the imagery); the SCALE BAR does
    // not (rotation preserves pixel lengths, so metres/px is angle-invariant).
    ctx.save();
    ctx.translate(30, 52);
    ctx.rotate(rot);
    ctx.fillStyle = tk("--canvas-contrast-max");
    ctx.strokeStyle = tk("--canvas-ink");
    ctx.beginPath();
    ctx.moveTo(0, -8);
    ctx.lineTo(-6, 10);
    ctx.lineTo(0, 4);
    ctx.lineTo(6, 10);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.font = "bold 12px sans-serif";
    ctx.fillText("N", -5, -12);
    ctx.restore();
    ctx.fillStyle = tk("--canvas-contrast-max");
    const L = S > 18 ? 2 : S > 7 ? 5 : S > 3 ? 20 : 50;
    ctx.fillRect(20, CH - 44, S * L, 5);
    ctx.font = "11px sans-serif";
    ctx.fillText(`${L} m`, 20 + (S * L) / 2 - 8, CH - 50);
    // E59 rule 2: the exact credit of the pixels ON SCREEN (licence condition),
    // plus vintage where the source exposes it
    const credit = `${attributionFor(basemapRef.current, z)} · ${p.lat.toFixed(4)}, ${p.lon.toFixed(4)}`;
    ctx.fillStyle = tk("--canvas-contrast-strong");
    ctx.font = "10px sans-serif";
    ctx.fillText(credit, Math.max(8, CW - 8 - ctx.measureText(credit).width), CH - 10);
    if (bmSource.kind === "google_solar") {
      // display_context no-map rule: Google mark + attribution whenever these
      // pixels show. Text wordmark until the official logo asset is staged
      // (deploy item — flagged in the report).
      ctx.font = "bold 13px sans-serif";
      ctx.fillStyle = tk("--canvas-contrast-max");
      ctx.fillText("Google", 20, CH - 10);
    }

    // E62.1 — the view is magnified past the imagery's native resolution: the
    // planner is looking at interpolation, not data, and must know WHICH limit
    // they've hit (zoom itself stays available for precise vertex placement —
    // it's the imagery that has a ceiling). Never silent blur: Romina read
    // 7×-magnified 20 cm pixels as "degraded imagery".
    const limit = imageryAtLimit(basemapRef.current, S, p.lat);
    if (limit) {
      const msg =
        limit.resolutionM != null
          ? t("canvas.nativeResLimit").replace("{res}", String(Math.round(limit.resolutionM * 100)))
          : t("canvas.nativeResLimitGeneric");
      ctx.font = "bold 11px sans-serif";
      const w = ctx.measureText(msg).width;
      const bx = (CW - w) / 2 - 10;
      ctx.fillStyle = tk("--canvas-ink");
      ctx.globalAlpha = 0.82;
      ctx.fillRect(bx, 10, w + 20, 22);
      ctx.globalAlpha = 1;
      ctx.fillStyle = tk("--canvas-contrast-max");
      ctx.fillText(msg, bx + 10, 25);
    }
  }

  function labelOf(s: CanvasSurface): string {
    return s.id.slice(0, 4) === "srf-" ? s.id : s.id.slice(0, 6);
  }

  function toLocal(ev: { clientX: number; clientY: number }): { x: number; y: number } {
    const cv = ref.current!;
    const r = cv.getBoundingClientRect();
    const px = (ev.clientX - r.left) * (CW / r.width);
    const py = (ev.clientY - r.top) * (CH / r.height);
    // E60: EVERY pointer path maps screen→world through the single inverse —
    // an un-inverted path fails only when rotated, so none exists.
    return screenToWorld({ x: px, y: py }, stateRef.current.view, CW, CH);
  }

  /** Hit-test modules of a surface at a world point (grid-frame transform). */
  function hitModule(s: CanvasSurface, world: { x: number; y: number }) {
    const layout = layoutFor(s, false);
    if (!layout) return null;
    const gp =
      layout.gridRotationDeg !== 0
        ? rotatePoint(world, layout.gridPivot, -layout.gridRotationDeg)
        : world;
    return layout.modules.find(
      (m) => gp.x >= m.x && gp.x <= m.x + m.w && gp.y >= m.y && gp.y <= m.y + m.h,
    ) ?? null;
  }

  useEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    let down: { x: number; y: number } | null = null;
    let moved = false;

    /** E42.2 — hit-test the active surface's boundary vertices at screen scale (~9 px). */
    const hitVertex = (ev: MouseEvent): { surfaceId: string; index: number } | null => {
      const p = stateRef.current;
      if (p.mode !== "site" || !p.activeSurfaceId || !p.onGeometryChange) return null;
      const s = p.surfaces.find((x) => x.id === p.activeSurfaceId);
      if (!s) return null;
      const world = toLocal(ev);
      for (let i = 0; i < s.geometry.vertices.length; i++) {
        const v = s.geometry.vertices[i]!;
        if (Math.hypot(world.x - v.x, world.y - v.y) * p.view.S < 9) return { surfaceId: s.id, index: i };
      }
      return null;
    };

    const onDown = (ev: MouseEvent) => {
      down = { x: ev.clientX, y: ev.clientY };
      moved = false;
      const p = stateRef.current;
      if (ev.button === 0) {
        const hv = hitVertex(ev);
        if (hv) {
          vertexDragRef.current = hv; // grab a boundary handle instead of panning/drawing
          return;
        }
      }
      if (p.mode === "layout" && p.onMoveModule && p.activeSurfaceId) {
        const s = p.surfaces.find((x) => x.id === p.activeSurfaceId);
        if (s) {
          const world = toLocal(ev);
          const hit = hitModule(s, world);
          if (hit && !s.removedKeys?.has(hit.key)) {
            const layout = layoutFor(s, false)!;
            const startLocal =
              layout.gridRotationDeg !== 0
                ? rotatePoint(world, layout.gridPivot, -layout.gridRotationDeg)
                : world;
            dragRef.current = {
              surfaceId: s.id,
              key: hit.key,
              startLocal,
              base: s.movedOffsets?.[hit.key] ?? { dx: 0, dy: 0 },
              live: { dx: 0, dy: 0 },
            };
          }
        }
      }
    };

    const onMove = (ev: MouseEvent) => {
      if (!down) return;
      const p = stateRef.current;
      const dx = ev.clientX - down.x;
      const dy = ev.clientY - down.y;
      if (!moved && Math.abs(dx) + Math.abs(dy) <= 4) return;
      moved = true;

      // E42.2 — dragging a boundary vertex: commit live through onGeometryChange
      const vd = vertexDragRef.current;
      if (vd) {
        const s = p.surfaces.find((x) => x.id === vd.surfaceId);
        if (s && p.onGeometryChange) {
          const world = toLocal(ev);
          const vertices = s.geometry.vertices.map((v, i) =>
            i === vd.index ? { x: +world.x.toFixed(2), y: +world.y.toFixed(2) } : v,
          );
          p.onGeometryChange(s.id, { ...s.geometry, vertices });
        }
        return;
      }

      const d = dragRef.current;
      if (d) {
        // dragging a module: convert cursor to grid frame of that surface
        const s = p.surfaces.find((x) => x.id === d.surfaceId);
        if (s) {
          const layout = layoutFor(s, false)!;
          const world = toLocal(ev);
          const gp =
            layout.gridRotationDeg !== 0
              ? rotatePoint(world, layout.gridPivot, -layout.gridRotationDeg)
              : world;
          d.live = { dx: gp.x - d.startLocal.x, dy: gp.y - d.startLocal.y };
          draw();
        }
        return;
      }

      // panning — through panView: the world shift depends on the camera angle (E60)
      const r = cv.getBoundingClientRect();
      const k = CW / r.width;
      p.onViewChange(panView(p.view, dx * k, dy * k));
      down = { x: ev.clientX, y: ev.clientY };
    };

    const onUp = (ev: MouseEvent) => {
      const was = moved;
      const d = dragRef.current;
      const vd = vertexDragRef.current;
      dragRef.current = null;
      vertexDragRef.current = null;
      down = null;
      moved = false;
      const p = stateRef.current;

      // E42.2 — a vertex grab consumes the gesture: no pan, no new point on release
      if (vd) return;

      if (d && was) {
        // commit module move (snap to 1 cm)
        const dx = Math.round((d.base.dx + d.live.dx) * 100) / 100;
        const dy = Math.round((d.base.dy + d.live.dy) * 100) / 100;
        p.onMoveModule?.(d.surfaceId, d.key, dx, dy);
        return;
      }
      if (was) return; // pan ended

      // click actions
      const world = toLocal(ev);
      if (p.mode === "layout") {
        if (p.activeSurfaceId && p.onToggleModule) {
          const s = p.surfaces.find((x) => x.id === p.activeSurfaceId);
          if (s) {
            const hit = hitModule(s, world);
            if (hit) p.onToggleModule(s.id, hit.key);
          }
        }
        return;
      }
      // site mode drawing on the active surface
      if (!p.activeSurfaceId || !p.onGeometryChange) return;
      const s = p.surfaces.find((x) => x.id === p.activeSurfaceId);
      if (!s) return;
      if (p.tool === "draw") {
        const v = s.geometry.vertices;
        if (v.length > 2) {
          const a = v[0]!;
          if (Math.hypot(world.x - a.x, world.y - a.y) * p.view.S < 12) return;
        }
        p.onGeometryChange(s.id, {
          ...s.geometry,
          vertices: [...v, { x: +world.x.toFixed(2), y: +world.y.toFixed(2) }],
        });
      } else if (p.tool === "obstacle") {
        p.onGeometryChange(s.id, {
          ...s.geometry,
          obstacles: [
            ...s.geometry.obstacles,
            { center: { x: +world.x.toFixed(2), y: +world.y.toFixed(2) }, radiusM: 0.6, heightM: 1.2, kind: "chimney" },
          ],
        });
      }
    };

    const onWheel = (ev: WheelEvent) => {
      ev.preventDefault();
      const p = stateRef.current;
      const S = Math.max(1.5, Math.min(45, p.view.S * Math.exp(-ev.deltaY * 0.0012)));
      p.onViewChange({ ...p.view, S });
    };

    const onCtx = (ev: MouseEvent) => {
      ev.preventDefault();
      const p = stateRef.current;
      if (p.mode !== "site" || !p.activeSurfaceId || !p.onGeometryChange) return;
      const s = p.surfaces.find((x) => x.id === p.activeSurfaceId);
      if (!s) return;
      // E42.3 — right-click on a boundary handle deletes THAT vertex (never below a triangle
      // unless the outline is still being drawn); otherwise obstacles are removed as before
      const hv = hitVertex(ev);
      if (hv && (s.geometry.vertices.length > 3 || s.geometry.vertices.length <= 2)) {
        p.onGeometryChange(s.id, {
          ...s.geometry,
          vertices: s.geometry.vertices.filter((_, i) => i !== hv.index),
        });
        return;
      }
      const q = toLocal(ev);
      p.onGeometryChange(s.id, {
        ...s.geometry,
        obstacles: s.geometry.obstacles.filter(
          (o) => Math.hypot(o.center.x - q.x, o.center.y - q.y) > o.radiusM + 0.3,
        ),
      });
    };

    // E42.1 — undo last boundary point via Cmd/Ctrl+Z (button variant below)
    const onKey = (ev: KeyboardEvent) => {
      if (!(ev.metaKey || ev.ctrlKey) || ev.key.toLowerCase() !== "z" || ev.shiftKey) return;
      const t = ev.target as HTMLElement | null;
      if (t && ["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName)) return; // never steal text-field undo
      if (undoLastPoint()) ev.preventDefault();
    };
    window.addEventListener("keydown", onKey);

    cv.addEventListener("mousedown", onDown);
    cv.addEventListener("mousemove", onMove);
    cv.addEventListener("mouseup", onUp);
    cv.addEventListener("wheel", onWheel, { passive: false });
    cv.addEventListener("contextmenu", onCtx);
    return () => {
      window.removeEventListener("keydown", onKey);
      cv.removeEventListener("mousedown", onDown);
      cv.removeEventListener("mousemove", onMove);
      cv.removeEventListener("mouseup", onUp);
      cv.removeEventListener("wheel", onWheel);
      cv.removeEventListener("contextmenu", onCtx);
    };
  }, []);

  /* --------------- E42.0/1 — on-map controls (affordances, not hints) --------------- */

  function zoomBy(factor: number): void {
    const p = stateRef.current;
    p.onViewChange({ ...p.view, S: +Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, p.view.S * factor)).toFixed(2) });
  }

  function fit(): void {
    const p = stateRef.current;
    // E60: fit changes zoom/centre, not the camera angle the planner chose
    p.onViewChange({ ...fitViewFor(p.surfaces), rotationDeg: p.view.rotationDeg ?? 0 });
  }

  /* E60 — camera rotation (Romina: "like I would do with Google Earth").
   * Visible affordances, not hint text (E42's lesson). Display-only: nothing
   * computed from it, nothing persisted (pinned by test). */
  function rotateBy(deg: number): void {
    const p = stateRef.current;
    p.onViewChange(rotateView(p.view, deg));
  }
  function toNorth(): void {
    const p = stateRef.current;
    p.onViewChange(resetNorth(p.view));
  }

  /** Remove the last boundary point of the active surface (draw mode). Returns true if it acted. */
  function undoLastPoint(): boolean {
    const p = stateRef.current;
    if (p.mode !== "site" || p.tool !== "draw" || !p.activeSurfaceId || !p.onGeometryChange) return false;
    const s = p.surfaces.find((x) => x.id === p.activeSurfaceId);
    if (!s || s.geometry.vertices.length === 0) return false;
    p.onGeometryChange(s.id, { ...s.geometry, vertices: s.geometry.vertices.slice(0, -1) });
    return true;
  }

  const p = props;
  const activeSurface = p.surfaces.find((x) => x.id === p.activeSurfaceId);
  const canUndo =
    p.mode === "site" && p.tool === "draw" && (activeSurface?.geometry.vertices.length ?? 0) > 0;

  return (
    <div className="canvas-stage">
      <canvas ref={ref} className="designer" />
      {/* E42.0 — visible on-canvas map controls: the product owner herself did not
          discover wheel-zoom from the hint text; buttons ON the map fix that.
          Labels via the E45 catalogue (keys: canvas.zoomIn/zoomOut/fit/undoPoint). */}
      <div className="map-controls" role="group" aria-label={t("canvas.mapControls")}>
        <button type="button" className="map-btn" title={t("canvas.zoomIn")} aria-label={t("canvas.zoomIn")} onClick={() => zoomBy(1.3)}>＋</button>
        <button type="button" className="map-btn" title={t("canvas.zoomOut")} aria-label={t("canvas.zoomOut")} onClick={() => zoomBy(1 / 1.3)}>−</button>
        <button type="button" className="map-btn map-btn-fit" title={t("canvas.fit")} aria-label={t("canvas.fit")} onClick={fit}>⤢</button>
        {/* E60 — view rotation ("Ansicht"), deliberately distinct from the
            module-grid rotation ("Modulraster") in the toolbar */}
        <button type="button" className="map-btn" title={t("canvas.rotateViewLeft")} aria-label={t("canvas.rotateViewLeft")} onClick={() => rotateBy(-15)}>⟲</button>
        <button type="button" className="map-btn" title={t("canvas.rotateViewRight")} aria-label={t("canvas.rotateViewRight")} onClick={() => rotateBy(15)}>⟳</button>
        <button
          type="button"
          className="map-btn map-btn-north"
          title={t("canvas.resetNorth")}
          aria-label={t("canvas.resetNorth")}
          disabled={(p.view.rotationDeg ?? 0) === 0}
          onClick={toNorth}
        >
          N{(p.view.rotationDeg ?? 0) !== 0 ? ` ${Math.round(p.view.rotationDeg ?? 0)}°` : ""}
        </button>
      </div>
      {p.mode === "site" && p.tool === "draw" && (
        <div className="map-controls map-controls-left">
          <button
            type="button"
            className="map-btn map-btn-undo"
            title={t("canvas.undoPoint")}
            aria-label={t("canvas.undoPoint")}
            disabled={!canUndo}
            onClick={undoLastPoint}
          >
            {t("canvas.undoPointShort")}
          </button>
        </div>
      )}
    </div>
  );
}
